import type { PRNG } from 'seedrandom';
import type { HexTileCoordinates, HexTileType } from '../../model/types';
import type { HexMapNoise } from '../types';

type PerlinIslandsNoiseOptions = {
  frequency?: number;
  octaves?: number;
  persistence?: number;
  lacunarity?: number;
  islandRadius?: number;
  coastlineFalloff?: number;
  seaLevel?: number;
  noiseWeight?: number;
  maskWeight?: number;
  heightOffset?: number;
  islandCenter?: HexTileCoordinates;
  /** Height band just above sea level rendered as beach. */
  beachLevel?: number;
  /** Frequency of the independent fertility noise that places fields. */
  fertilityFrequency?: number;
  /** Inland, non-forest tiles with fertility above this become fields (else grass). */
  fieldThreshold?: number;
  /** Frequency of the independent woodland noise that places forests. */
  woodlandFrequency?: number;
  /** Inland tiles with woodland above this become forest. */
  forestThreshold?: number;
  /** Smallest amount of resource a tile can hold. */
  resourceMin?: number;
  /** Largest amount of resource a tile can hold. */
  resourceMax?: number;
};

const DEFAULT_OPTIONS: Required<PerlinIslandsNoiseOptions> = {
  frequency: 0.12,
  octaves: 4,
  persistence: 0.52,
  lacunarity: 2,
  islandRadius: 10,
  coastlineFalloff: 2.1,
  seaLevel: 0.1,
  noiseWeight: 0.75,
  maskWeight: 0.85,
  heightOffset: 0,
  islandCenter: { q: 0, r: 0 },
  beachLevel: 0.06,
  fertilityFrequency: 0.18,
  fieldThreshold: 0.04,
  woodlandFrequency: 0.16,
  forestThreshold: 0.12,
  resourceMin: 4,
  resourceMax: 20,
};

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + t * (b - a);
}

function grad(hash: number, x: number, y: number): number {
  const h = hash & 3;
  if (h === 0) return x + y;
  if (h === 1) return -x + y;
  if (h === 2) return x - y;
  return -x - y;
}

function createPermutation(generator: PRNG): Uint8Array {
  const base = new Uint8Array(256);
  for (let i = 0; i < 256; i++) base[i] = i;

  for (let i = 255; i > 0; i--) {
    const j = Math.floor(generator() * (i + 1));
    const tmp = base[i];
    base[i] = base[j];
    base[j] = tmp;
  }

  const perm = new Uint8Array(512);
  for (let i = 0; i < 512; i++) perm[i] = base[i & 255];
  return perm;
}

function perlin2(x: number, y: number, perm: Uint8Array): number {
  const xi = Math.floor(x) & 255;
  const yi = Math.floor(y) & 255;

  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);

  const u = fade(xf);
  const v = fade(yf);

  const aa = perm[perm[xi] + yi];
  const ab = perm[perm[xi] + yi + 1];
  const ba = perm[perm[xi + 1] + yi];
  const bb = perm[perm[xi + 1] + yi + 1];

  const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
  const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);
  return lerp(x1, x2, v);
}

function fractalPerlin2(
  x: number,
  y: number,
  perm: Uint8Array,
  octaves: number,
  persistence: number,
  lacunarity: number
): number {
  let amplitude = 1;
  let frequency = 1;
  let sum = 0;
  let maxAmplitude = 0;

  for (let i = 0; i < octaves; i++) {
    sum += perlin2(x * frequency, y * frequency, perm) * amplitude;
    maxAmplitude += amplitude;
    amplitude *= persistence;
    frequency *= lacunarity;
  }

  if (maxAmplitude === 0) return 0;
  return sum / maxAmplitude;
}

function getIslandMask(distance: number, islandRadius: number, coastlineFalloff: number): number {
  if (islandRadius <= 0) return 1;
  const normalizedDistance = distance / islandRadius;
  return clamp01(1 - normalizedDistance ** coastlineFalloff);
}

/**
 * A tile's resource amount: a bell curve over [min, max] centred on their
 * midpoint (the mean of three uniform draws), rounded to a whole number.
 */
function sampleResourceValue(generator: PRNG, min: number, max: number): number {
  const t = (generator() + generator() + generator()) / 3;
  return Math.round(min + t * (max - min));
}

function getTileType(
  height: number,
  woodland: number,
  fertility: number,
  config: Required<PerlinIslandsNoiseOptions>
): HexTileType {
  if (height < 0) return 'water';
  if (height < config.beachLevel) return 'beach';
  // Forests and fields follow their own noises so each forms patches
  // independent of height; whatever neither claims is open grass ('default').
  if (woodland > config.forestThreshold) return 'forest';
  if (fertility > config.fieldThreshold) return 'field';
  return 'default';
}

/**
 * Perlin-based island terrain.
 * Combines fBm Perlin with radial falloff so map edges trend toward water.
 * Height picks water / beach / inland; independent woodland and fertility
 * noises then place forest and field patches, leaving the rest as grass.
 */
function createPerlinIslandsNoise(options: PerlinIslandsNoiseOptions = {}): HexMapNoise {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let permutation: Uint8Array | null = null;
  let fertilityPermutation: Uint8Array | null = null;
  let woodlandPermutation: Uint8Array | null = null;

  return {
    id: 'perlin-islands',
    sample(coordinates: HexTileCoordinates, { generator }) {
      if (!permutation) {
        permutation = createPermutation(generator);
        fertilityPermutation = createPermutation(generator);
        woodlandPermutation = createPermutation(generator);
      }

      const { q, r } = coordinates;
      const x = q + r * 0.5;
      const y = r * 0.8660254037844386;
      const baseNoise = fractalPerlin2(
        x * config.frequency,
        y * config.frequency,
        permutation,
        config.octaves,
        config.persistence,
        config.lacunarity
      );
      const centeredQ = q - config.islandCenter.q;
      const centeredR = r - config.islandCenter.r;
      const distance = Math.sqrt(centeredQ * centeredQ + centeredR * centeredR);
      const islandMask = getIslandMask(distance, config.islandRadius, config.coastlineFalloff);
      const height =
        baseNoise * config.noiseWeight +
        islandMask * config.maskWeight -
        config.seaLevel +
        config.heightOffset;

      const fertility = fractalPerlin2(
        x * config.fertilityFrequency,
        y * config.fertilityFrequency,
        fertilityPermutation as Uint8Array,
        2,
        0.5,
        2
      );

      return {
        type: getTileType(
          height,
          fractalPerlin2(
            x * config.woodlandFrequency,
            y * config.woodlandFrequency,
            woodlandPermutation as Uint8Array,
            2,
            0.5,
            2
          ),
          fertility,
          config
        ),
        resourceValue: sampleResourceValue(generator, config.resourceMin, config.resourceMax),
      };
    },
  };
}

export type { PerlinIslandsNoiseOptions };
export { createPerlinIslandsNoise };
